import { CheckCircleFilled, CloseOutlined, FolderOpenOutlined, FolderOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Tabs, List, Typography, Tooltip, Empty } from 'antd';
import { DateTime } from 'luxon';
import { MutableRefObject, RefObject, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    POLLING_INTERVAL_ACTIVE,
    POLLING_INTERVAL_BACKGROUND,
    POLLING_INTERVAL_NIGHT,
    DEFAULT_ARCHIVE_DAYS
} from '../../constants';
import { INewsChatViewDTO } from '../../libs/coreapi-dto/service/newsChat';
import { UISettingsManager } from '../../libs/ui-settings-manager/UISettingsManager';
import { NewsChatDataProvider } from '../../Services/DataProviders/NewsChatDataProvider';
import { playAlertSound } from '../../system/functions/beepSound';
import { useTimeFormatter } from '../../system/hooks/useTimeFormatter';
import { useAppContext } from '../../system/providers/appContextProvider';
import { useNews } from '../../system/providers/newsContext';
import { useUserContext } from '../../system/providers/userContextProvider';
import ChatSettingsButton from './components/chatSettingsButton/ChatSettingsButton';
import FloatingChatButton from './components/floatingChatButton/FloatingChatButton';
import styles from './NewsChatModal.module.scss'
import NewsDescription from './components/NewsDescription';
import NewsFilters from './components/newsFilters/NewsFilters';
import { useNewsFilters } from './hooks/useNewsFilters';
import { useFilteredNews } from './hooks/useFilteredNews';
import { getNewsDate } from './utils/newsUtils';

enum RedmineNewsPriority { Low = 1, Medium = 2, High = 3, Critical = 4 }

const PRIORITY_STYLES = {
    [RedmineNewsPriority.Low]: styles.priorityLow,
    [RedmineNewsPriority.Medium]: styles.priorityMedium,
    [RedmineNewsPriority.High]: styles.priorityHigh,
    [RedmineNewsPriority.Critical]: styles.priorityCritical,
};

interface INewsChatModalProps {
    tourRefs?: {
        newsButton?: RefObject<HTMLDivElement>
        settingsButton?: RefObject<HTMLDivElement>
        settingsPanel?: RefObject<HTMLDivElement>
    }
    tourActions?: {
        showButton?: MutableRefObject<(() => void) | null>
        openModal?: MutableRefObject<(() => void) | null>
        closeModal?: MutableRefObject<(() => void) | null>
        openSettings?: MutableRefObject<(() => void) | null>
        closeSettings?: MutableRefObject<(() => void) | null>
    }
}

const NewsChatModal = (props: INewsChatModalProps) => {
    const appContext = useAppContext();
    const userContext = useUserContext();
    const newsChatDataProvider = new NewsChatDataProvider(appContext.coreApiService);

    const {
        news: contextNews,
        unreadCount: contextUnreadCount,
        updateNews,
        markAsViewed: contextMarkAsViewed,
        isLoading: contextIsLoading,
        setIsLoading: setContextIsLoading
    } = useNews();

    const [open, setOpen] = useState(false);
    const [tourButtonVisible, setTourButtonVisible] = useState(false);
    const [activeTab, setActiveTab] = useState('news');
    const [isPolling, setIsPolling] = useState(true);
    const pollingRef = useRef<NodeJS.Timeout>();
    const isActiveTabRef = useRef(true);
    const [settings, setSettings] = useState(() => UISettingsManager.getSettings().chat);
    const { formatEstimatedHour } = useTimeFormatter();
    const lastUnreadCountRef = useRef<number>(0);
    const isSuperUser = userContext.userRoles.includes('SuperUser');
    const isGlobalAdmin = userContext.userRoles
            .flat(Infinity)
            .filter(role => typeof role === 'string')
            .map(role => role.toLowerCase())    
            .includes('globaladmin');

    // Refs
    const listRef = useRef<HTMLDivElement>(null);
    const newsItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const pendingScrollToNewsId = useRef<string | null>(null);
    const [showArchive, setShowArchive] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const {
        filters,
        setFilters,
        resetFilters,
    } = useNewsFilters();

    const { t } = useTranslation();
    const baseT = (value: string) => t('chat.' + value);
    const archiveDays = settings.archiveDays || DEFAULT_ARCHIVE_DAYS;

    const activeNews = useMemo(() => {
        const archiveLimit = DateTime.now().minus({ days: archiveDays });

        return contextNews.filter(item => {
            if (!item.isViewed) {
                return true;
            }

            const newsDate = getNewsDate(item);

            if (!newsDate.isValid) {
                return true;
            }

            return newsDate >= archiveLimit;
        });
    }, [contextNews, archiveDays]);

    const archivedNews = useMemo(() => {
        const now = DateTime.now();
        const archiveLimit = now.minus({ days: archiveDays });

        return contextNews.filter(item => {
            if (!item.isViewed) return false;

            const newsDate = getNewsDate(item);

            if (!newsDate.isValid) {
                return false;
            }

            return newsDate < archiveLimit;
        });
    }, [contextNews, archiveDays]);

    const filteredActiveNews = useFilteredNews(activeNews, filters);
    const filteredArchivedNews = useFilteredNews(archivedNews, filters);

    useEffect(() => {
        lastUnreadCountRef.current = contextUnreadCount;
    }, [contextUnreadCount]);

    const getModalPosition = () => {
        if (settings.chatPosition === 'top') {
            return {
                top: 65,
                right: 35,
                bottom: 'auto',
            };
        }

        return {
            bottom: 145,
            right: 35,
            top: 'auto',
        };
    };

    const handleMarkAsViewed = useCallback((idGlobal: string) => {
        try {
            newsChatDataProvider.markNewAsViewed(idGlobal);
            contextMarkAsViewed(idGlobal);
        } catch (e) {
            console.error(e);
        }
    }, [newsChatDataProvider, contextMarkAsViewed]);

    const handleMarkAsReadClick = useCallback((e: React.MouseEvent, idGlobal: string) => {
        e.stopPropagation();
        handleMarkAsViewed(idGlobal);
    }, [handleMarkAsViewed]);

    const fetchNews = useCallback(async () => {
        try {
            if (!isActiveTabRef.current || isSuperUser || isGlobalAdmin) return;

            setContextIsLoading(true);
            newsChatDataProvider.getActualNews((entities) => {

                const newUnreadEntities = entities.filter(n => !n.isViewed);
                const currentUnreadIds = new Set(
                    contextNews.filter(n => !n.isViewed).map(n => n.idGlobal)
                );
                const brandNewUnread = newUnreadEntities.filter(
                    n => !currentUnreadIds.has(n.idGlobal)
                );

                if (brandNewUnread.length > 0 && settings.sound === true && !open) {
                    playAlertSound('info');
                }

                // Обновляем новости в контексте (синхронизация с localStorage)
                updateNews(entities);
                setContextIsLoading(false);
            });
        } catch (error) {
            console.error(error);
            setContextIsLoading(false);
        }
    }, [newsChatDataProvider, updateNews, setContextIsLoading, contextNews, settings.sound, open]);

    // Инициализация: загружаем новости при монтировании
    useEffect(() => {
        fetchNews();
    }, []);

    // Polling
    useEffect(() => {
        if (!isPolling) return;

        const getCurrentInterval = () => {
            if (open) return POLLING_INTERVAL_ACTIVE;

            const hours = new Date().getHours();
            if (hours >= 23 || hours < 6) return POLLING_INTERVAL_NIGHT;

            return POLLING_INTERVAL_BACKGROUND;
        };

        const startPolling = () => {
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
            }

            const interval = getCurrentInterval();

            pollingRef.current = setTimeout(() => {
                fetchNews();
                startPolling(); // Рекурсивный вызов для следующего опроса
            }, interval);
        };

        startPolling();

        return () => {
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
            }
        };
    }, [open, isPolling]);

    // Visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            isActiveTabRef.current = !document.hidden;

            // Если вкладка неактивна (пользователь переключился)
            if (document.hidden) {
                // Паузим polling на время неактивности
                setIsPolling(false);
            } else {
                // Вкладка снова активна - возобновляем polling
                setIsPolling(true);
                fetchNews();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Settings change
    useEffect(() => {
        const handleSettingsChange = () => {
            const uiSettings = UISettingsManager.getSettings();
            setSettings(uiSettings.chat);
        };

        window.addEventListener('uiSettingsChanged', handleSettingsChange);
        return () => window.removeEventListener('uiSettingsChanged', handleSettingsChange);
    }, []);

    const scrollToNews = useCallback((newsId: string) => {
        const newsElement = newsItemRefs.current.get(newsId);
        if (newsElement && listRef.current) {
            // Плавная прокрутка к элементу
            newsElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });

            // Добавляем временный класс для подсветки
            newsElement.classList.add(styles.highlighted);
            setTimeout(() => {
                newsElement.classList.remove(styles.highlighted);
            }, 2000);
        }
    }, []);

    // глобальный обработчик для открытия чата (напр. из карусели)
    useEffect(() => {
        const handleOpenNewsChat = (event: CustomEvent) => {
            setOpen(true);
            // Если нужно открыть конкретную новость
            if (event.detail?.newsId) {
                const newsId = event.detail.newsId;

                // Проверяем, загружены ли уже новости
                const newsExists = contextNews.some(n => n.idGlobal === newsId);

                if (newsExists) {
                    // Если новость уже в списке, прокручиваем к ней
                    setTimeout(() => {
                        setActiveTab('news');
                        scrollToNews(newsId);
                    }, 300); // Задержка для открытия анимации модального окна
                } else {
                    // Если новость еще не загружена, запоминаем ID для прокрутки после загрузки
                    pendingScrollToNewsId.current = newsId;
                }
            }
        };

        window.addEventListener('openNewsChat', handleOpenNewsChat as EventListener);
        return () => {
            window.removeEventListener('openNewsChat', handleOpenNewsChat as EventListener);
        };
    }, [contextNews, scrollToNews]);

    // Прокрутка к сохраненной новости после загрузки данных
    useEffect(() => {
        if (pendingScrollToNewsId.current && contextNews.length > 0 && open) {
            const newsId = pendingScrollToNewsId.current;
            const newsExists = contextNews.some(n => n.idGlobal === newsId);

            if (newsExists) {
                setTimeout(() => {
                    scrollToNews(newsId);
                    pendingScrollToNewsId.current = null;
                }, 300);
            }
        }
    }, [contextNews, open, scrollToNews]);

    const handleOpen = useCallback(() => {
        setTourButtonVisible(true);
        setOpen(true);
        setActiveTab('news');
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setTourButtonVisible(false);
    }, []);

    useEffect(() => {
        if (props.tourActions?.showButton) {
            props.tourActions.showButton.current = () => setTourButtonVisible(true)
        }

        if (props.tourActions?.openModal) {
            props.tourActions.openModal.current = handleOpen
        }

        if (props.tourActions?.closeModal) {
            props.tourActions.closeModal.current = handleClose
        }

        return () => {
            if (props.tourActions?.showButton) {
                props.tourActions.showButton.current = null
            }

            if (props.tourActions?.openModal) {
                props.tourActions.openModal.current = null
            }

            if (props.tourActions?.closeModal) {
                props.tourActions.closeModal.current = null
            }
        }
    }, [props.tourActions, handleOpen, handleClose])

    const toggleArchive = () => {
        if (showArchive) {
            setShowArchive(false);
            setActiveTab('news');
        } else {
            setShowArchive(true);
            setActiveTab('newsArc');
        }
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (modalRef.current?.contains(target)) {
                return;
            }

            handleClose();
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [open, handleClose]);

    const renderNewsList = (newsList: INewsChatViewDTO[]) => {
        if (contextIsLoading && contextNews.length === 0) {
            return (
                <Empty
                    description={baseT('loading')}
                />
            );
        }
        if (newsList.length === 0) {
            return (
                <Empty
                    description={baseT('nothingFound')}
                >
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            resetFilters();
                        }}>
                        {baseT('clearFilters')}
                    </Button>
                </Empty>
            );
        }

        return (
            <div ref={listRef} className={styles.newsListContainer}>
                <List<INewsChatViewDTO>
                    dataSource={newsList}
                    renderItem={item => {
                        const isViewed = item.isViewed;
                        return (
                            <div
                                key={item.idGlobal}
                                ref={(el) => {
                                    if (el) {
                                        newsItemRefs.current.set(item.idGlobal, el);
                                        el.dataset.newsId = item.idGlobal;
                                    } else {
                                        newsItemRefs.current.delete(item.idGlobal);
                                    }
                                }}
                            >
                                <List.Item
                                    key={item.idGlobal}
                                    style={{ cursor: item.isViewed ? 'not-allowed' : 'pointer' }}
                                    className={`${styles.newsItem} ${item.isViewed ? styles.viewed : ''}`}
                                >
                                    <div className={`${styles.newsItemStrip} ${PRIORITY_STYLES[item.priority]}`} />

                                    <List.Item.Meta
                                        title={
                                            <Typography.Text strong>
                                                <div className={styles.headerNewsItem}>
                                                    <div className={styles.startDateNewsItem}>
                                                        {getNewsDate(item).toFormat('dd.MM.yyyy')}
                                                    </div>
                                                    <div className={styles.statusIcon}>
                                                        {isViewed ? (
                                                            <Tooltip
                                                                title={baseT('read')}
                                                                getPopupContainer={() => document.body}
                                                                placement="topRight"
                                                            >
                                                                <CheckCircleFilled
                                                                    className={styles.readIcon}
                                                                />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip
                                                                title={baseT('markAsRead')}
                                                                getPopupContainer={() => document.body}
                                                                placement="topRight"
                                                            >
                                                                <MailOutlined
                                                                    className={styles.unreadIcon}
                                                                    onClick={(e) => handleMarkAsReadClick(e, item.idGlobal)}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </div>
                                                {item.subject}
                                            </Typography.Text>
                                        }
                                        description={
                                            <div>
                                                <NewsDescription value={item.description} />
                                                {item.estimatedHour > 0 && (
                                                    <Typography.Text
                                                        type="secondary"
                                                        style={{
                                                            fontSize: '11px',
                                                            display: 'block',
                                                            marginTop: '8px',
                                                            color: '#adb5bd'
                                                        }}
                                                    >
                                                        ⏱ {formatEstimatedHour(item.estimatedHour)}
                                                    </Typography.Text>
                                                )}

                                            </div>
                                        }
                                    />
                                </List.Item>
                            </div>
                        )
                    }}
                />
            </div>
        );
    };

    const newsTabContent = (
        <div className={styles.newsTabContent}>
            <NewsFilters
                value={filters}
                onChange={setFilters}
                onReset={resetFilters}
            />
            {renderNewsList(filteredActiveNews)}
        </div>
    );

    const newsArcTabContent = (
        <div className={styles.newsTabContent}>
            <NewsFilters
                value={filters}
                onChange={setFilters}
                onReset={resetFilters}
            />
            {renderNewsList(filteredArchivedNews)}
        </div>
    );

    const items = [
        {
            label: `${baseT('newsTitle')} (${activeNews.filter(n => !n.isViewed).length})`,
            key: 'news',
            children: newsTabContent,
        },
    ];

    if (showArchive) {
        items.push({
            label: `${baseT('newsArcTitle')} (${archivedNews.length})`,
            key: 'newsArc',
            children: newsArcTabContent,
        });
    }

    if (isSuperUser || isGlobalAdmin) return <></>

    return (
        <>
            <FloatingChatButton
                buttonRef={props.tourRefs?.newsButton}
                unreadCount={contextUnreadCount}
                onClick={open ? handleClose : handleOpen}
                isOpen={open}
                forceVisible={tourButtonVisible}
            />
            <div
                ref={modalRef}
                className={`${styles.modalWindow} ${open ? styles.visible : ''}`}
                style={{
                    ...(open && { opacity: settings.opacity }),
                    ...getModalPosition()
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={items}
                    type="card"
                    tabBarExtraContent={{
                        right: (
                            <>
                                <ChatSettingsButton
                                    buttonRef={props.tourRefs?.settingsButton}
                                    panelRef={props.tourRefs?.settingsPanel}
                                    openActionRef={props.tourActions?.openSettings}
                                    closeActionRef={props.tourActions?.closeSettings}
                                />
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={handleClose}
                                    size="small"
                                    style={{ marginRight: 8 }}
                                />
                            </>
                        )
                    }}
                />

                <div className={styles.footer}>
                    <Button
                        type="link"
                        icon={showArchive ? <FolderOpenOutlined /> : <FolderOutlined />}
                        onClick={toggleArchive}
                    >
                        {showArchive
                            ? baseT('hideArchive')
                            : baseT('showArchive')}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default NewsChatModal;
